<template>
        <div class="flex flex-col lg:flex-row gap-4">
                <!-- Available Items Panel -->
                <div class="flex-1 min-w-0">
                        <div class="border border-gray-200 rounded-lg overflow-hidden">
                                <div class="bg-gray-50 px-4 py-3 border-b border-gray-200">
                                        <div class="flex items-center justify-between mb-2">
                                                <h4 class="text-sm font-semibold text-gray-700 m-0">Available</h4>
                                                <span class="text-xs text-gray-500">{{ availableItems.length }} items</span>
                                        </div>
                                        <input
                                                v-model="availableSearch"
                                                type="text"
                                                :placeholder="`Search ${itemLabel}...`"
                                                class="w-full py-2 px-3 border border-gray-300 rounded-md text-sm outline-none focus:border-orange-6 focus:ring-1 focus:ring-orange-5"
                                        >
                                </div>
                                <draggable
                                        v-model="localAvailableItems"
                                        :group="groupName"
                                        item-key="id"
                                        class="min-h-64 max-h-80 overflow-y-auto bg-white"
                                        ghost-class="opacity-50"
                                        @change="onAvailableChange"
                                >
                                        <template #item="{ element }">
                                                <div
                                                        v-show="matchesSearch(element, availableSearch)"
                                                        class="flex items-center gap-3 py-3 px-4 border-b border-gray-100 cursor-grab hover:bg-orange-1 transition-colors"
                                                >
                                                        <svg class="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                <path stroke-linecap="round" stroke-linejoin="round" d="M4 8h16M4 16h16" />
                                                        </svg>
                                                        <span class="text-sm text-gray-900 truncate flex-1">
                                                                {{ getDisplayName(element) }}
                                                        </span>
                                                        <button
                                                                type="button"
                                                                class="p-1 bg-transparent border-none cursor-pointer text-gray-400 hover:text-orange-7 transition-colors"
                                                                title="Add to selected"
                                                                @click.stop="selectItem(element)"
                                                        >
                                                                <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7" />
                                                                </svg>
                                                        </button>
                                                </div>
                                        </template>
                                </draggable>
                                <div v-if="filteredAvailableCount === 0" class="py-8 text-center text-gray-500 text-sm bg-white">
                                        {{ availableSearch ? 'No matching items' : 'No available items' }}
                                </div>
                        </div>
                </div>

                <!-- Arrow Buttons (Desktop) -->
                <div class="hidden lg:flex flex-col justify-center gap-2">
                        <button
                                type="button"
                                class="p-2 border border-gray-300 rounded-md bg-white hover:bg-orange-1 hover:border-orange-6 cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                :disabled="filteredAvailableCount === 0"
                                title="Move all visible to selected"
                                @click="moveAllVisibleToSelected"
                        >
                                <svg class="w-5 h-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                        <path stroke-linecap="round" stroke-linejoin="round" d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                                </svg>
                        </button>
                        <button
                                type="button"
                                class="p-2 border border-gray-300 rounded-md bg-white hover:bg-orange-1 hover:border-orange-6 cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                :disabled="filteredSelectedCount === 0"
                                title="Move all visible to available"
                                @click="moveAllVisibleToAvailable"
                        >
                                <svg class="w-5 h-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                        <path stroke-linecap="round" stroke-linejoin="round" d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                                </svg>
                        </button>
                </div>

                <!-- Selected Items Panel -->
                <div class="flex-1 min-w-0">
                        <div class="border border-orange-3 rounded-lg overflow-hidden">
                                <div class="bg-orange-1 px-4 py-3 border-b border-orange-3">
                                        <div class="flex items-center justify-between mb-2">
                                                <h4 class="text-sm font-semibold text-orange-7 m-0">Selected</h4>
                                                <span class="text-xs text-orange-6">{{ selectedItems.length }} items</span>
                                        </div>
                                        <input
                                                v-model="selectedSearch"
                                                type="text"
                                                :placeholder="`Search selected...`"
                                                class="w-full py-2 px-3 border border-orange-3 rounded-md text-sm outline-none focus:border-orange-6 focus:ring-1 focus:ring-orange-5"
                                        >
                                </div>
                                <draggable
                                        v-model="localSelectedItems"
                                        :group="groupName"
                                        item-key="id"
                                        class="min-h-64 max-h-80 overflow-y-auto bg-white"
                                        ghost-class="opacity-50"
                                        @change="onSelectedChange"
                                >
                                        <template #item="{ element }">
                                                <div
                                                        v-show="matchesSearch(element, selectedSearch)"
                                                        class="flex items-center gap-3 py-3 px-4 border-b border-orange-2 cursor-grab hover:bg-orange-1 transition-colors"
                                                >
                                                        <svg class="w-4 h-4 text-orange-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                <path stroke-linecap="round" stroke-linejoin="round" d="M4 8h16M4 16h16" />
                                                        </svg>
                                                        <span class="text-sm text-gray-900 truncate flex-1">
                                                                {{ getDisplayName(element) }}
                                                        </span>
                                                        <button
                                                                type="button"
                                                                class="p-1 bg-transparent border-none cursor-pointer text-gray-400 hover:text-red-600 transition-colors"
                                                                title="Remove from selected"
                                                                @click.stop="deselectItem(element)"
                                                        >
                                                                <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                                                                </svg>
                                                        </button>
                                                </div>
                                        </template>
                                </draggable>
                                <div v-if="filteredSelectedCount === 0" class="py-8 text-center text-gray-500 text-sm bg-white">
                                        {{ selectedSearch ? 'No matching items' : 'No items selected' }}
                                </div>
                        </div>
                </div>
        </div>
</template>

<script>
import { defineComponent, ref, computed, watch } from 'vue';
import draggable from 'vuedraggable';

export default defineComponent({
        name: 'DualListSelector',
        components: {
                draggable,
        },
        props: {
                allItems: {
                        type: Array,
                        required: true,
                },
                selectedIds: {
                        type: Object,
                        required: true,
                },
                itemLabel: {
                        type: String,
                        default: 'items',
                },
                displayField: {
                        type: String,
                        default: 'name',
                },
                secondaryField: {
                        type: String,
                        default: null,
                },
                groupName: {
                        type: String,
                        default: 'items',
                },
        },
        emits: ['select', 'deselect'],
        setup(props, { emit }) {
                const availableSearch = ref('');
                const selectedSearch = ref('');
                
                const localAvailableItems = ref([]);
                const localSelectedItems = ref([]);

                const availableItems = computed(() => {
                        return props.allItems.filter(item => !props.selectedIds[item.id]);
                });

                const selectedItems = computed(() => {
                        return props.allItems.filter(item => props.selectedIds[item.id]);
                });

                watch([() => props.allItems, () => props.selectedIds], () => {
                        localAvailableItems.value = [...availableItems.value];
                        localSelectedItems.value = [...selectedItems.value];
                }, { immediate: true, deep: true });

                const getDisplayName = (item) => {
                        let name = item[props.displayField] || '';
                        if (props.secondaryField && item[props.secondaryField]) {
                                name += ` ${item[props.secondaryField]}`;
                        }
                        return `${name} (${item.externalid || item.id})`;
                };

                const matchesSearch = (item, query) => {
                        if (!query) return true;
                        return getDisplayName(item).toLowerCase().includes(query.toLowerCase());
                };

                const filteredAvailableCount = computed(() => {
                        return localAvailableItems.value.filter(item => matchesSearch(item, availableSearch.value)).length;
                });

                const filteredSelectedCount = computed(() => {
                        return localSelectedItems.value.filter(item => matchesSearch(item, selectedSearch.value)).length;
                });

                const selectItem = (item) => {
                        emit('select', item);
                };

                const deselectItem = (item) => {
                        emit('deselect', item);
                };

                const onAvailableChange = (evt) => {
                        if (evt.added) {
                                emit('deselect', evt.added.element);
                        }
                };

                const onSelectedChange = (evt) => {
                        if (evt.added) {
                                emit('select', evt.added.element);
                        }
                };

                const moveAllVisibleToSelected = () => {
                        const itemsToMove = localAvailableItems.value.filter(item => matchesSearch(item, availableSearch.value));
                        itemsToMove.forEach(item => {
                                emit('select', item);
                        });
                };

                const moveAllVisibleToAvailable = () => {
                        const itemsToMove = localSelectedItems.value.filter(item => matchesSearch(item, selectedSearch.value));
                        itemsToMove.forEach(item => {
                                emit('deselect', item);
                        });
                };

                return {
                        availableSearch,
                        selectedSearch,
                        localAvailableItems,
                        localSelectedItems,
                        availableItems,
                        selectedItems,
                        filteredAvailableCount,
                        filteredSelectedCount,
                        getDisplayName,
                        matchesSearch,
                        selectItem,
                        deselectItem,
                        onAvailableChange,
                        onSelectedChange,
                        moveAllVisibleToSelected,
                        moveAllVisibleToAvailable,
                };
        },
});
</script>
